import { useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { euQuery } from "@/web/src/consultas";
import { useEscrevendo } from "@/web/src/escrevendo";
import { useNaoLida } from "@/web/src/lidas";
import { Icone } from "@/components/Icone";
import { Avatar } from "./Avatar";
import { comoChamar } from "@/lib/painel/telefone";
import type { ItemConversa } from "@/lib/telas/conversas";

type Filtro = "todas" | "equipe" | "voce" | "assistente";

/**
 * A coluna da esquerda. A lista chega pronta; aqui ficam a busca, o filtro e o
 * destaque do item aberto, que depende da URL.
 *
 * Três grupos, nesta ordem: quem espera um retorno que o assistente prometeu e
 * só a equipe pode dar; o que ESTÁ com você; o que o assistente está tocando.
 * Os dois primeiros têm alguém esperando do outro lado. Os chips em cima
 * filtram um grupo só — com a contagem, para ninguém precisar rolar e contar.
 */
export function ListaConversas({
  aguardandoEquipe,
  atendendoVoce,
  comOAgente,
  className,
}: {
  aguardandoEquipe: ItemConversa[];
  atendendoVoce: ItemConversa[];
  comOAgente: ItemConversa[];
  className?: string;
}) {
  const [busca, setBusca] = useState("");
  const [filtro, setFiltro] = useState<Filtro>("todas");
  const q = busca.trim().toLowerCase();
  const digitos = q.replace(/\D/g, "");
  const casa = (i: ItemConversa) =>
    !q ||
    (i.nome ?? "").toLowerCase().includes(q) ||
    (digitos.length > 0 && i.telefone.includes(digitos)) ||
    i.etiquetas.some((e) => e.includes(q));

  const grupos = [
    { id: "equipe" as const, titulo: "Aguardando a equipe", chip: "Aguardando equipe", itens: aguardandoEquipe, cor: "text-[var(--color-alerta)]" },
    { id: "voce" as const, titulo: "Você está atendendo", chip: "Você atende", itens: atendendoVoce, cor: "text-[var(--color-atencao)]" },
    { id: "assistente" as const, titulo: "Com o assistente", chip: "Com o assistente", itens: comOAgente, cor: "text-[var(--color-fraco)]" },
  ];
  const total = grupos.reduce((n, g) => n + g.itens.length, 0);
  const visiveis = grupos
    .filter((g) => filtro === "todas" || filtro === g.id)
    .map((g) => ({ ...g, itens: g.itens.filter(casa) }))
    .filter((g) => g.itens.length > 0);

  return (
    <aside
      className={
        "flex min-h-0 w-full shrink-0 flex-col border-[var(--color-borda)] bg-[var(--color-cartao)] md:w-[320px] md:border-r xl:w-[360px] " +
        (className ?? "")
      }
    >
      <header className="flex flex-col gap-3 px-4 pt-5 pb-2.5">
        <h1 className="text-[22px] font-bold tracking-tight text-[var(--color-tinta)]">Conversas</h1>
        <label className="flex h-10 items-center gap-2 rounded-full bg-[var(--color-painel)] px-3.5 text-[var(--color-fraco)] focus-within:shadow-[inset_0_0_0_1.5px_var(--color-marca)]">
          <Icone nome="lupa" className="h-4 w-4 shrink-0" />
          <input
            type="search"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar nome, telefone ou etiqueta"
            aria-label="Buscar conversa"
            className="min-w-0 flex-1 bg-transparent text-sm text-[var(--color-tinta)] outline-none placeholder:text-[var(--color-fraco)]"
          />
        </label>
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filtrar conversas">
          <Chip ativo={filtro === "todas"} onClick={() => setFiltro("todas")} rotulo="Todas" n={total} />
          {grupos.map((g) => (
            <Chip key={g.id} ativo={filtro === g.id} onClick={() => setFiltro(g.id)} rotulo={g.chip} n={g.itens.length} />
          ))}
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-4">
        {visiveis.map((g) => (
          <Grupo key={g.id} titulo={g.titulo} cor={g.cor} itens={g.itens} />
        ))}

        {total === 0 && (
          <p className="p-6 text-center text-[13px] leading-relaxed text-[var(--color-suave)]">
            Nenhuma conversa ainda. Assim que alguém escrever para o WhatsApp da clínica, a conversa aparece aqui, com o
            assistente já respondendo.
          </p>
        )}
        {total > 0 && visiveis.length === 0 && (
          <p className="p-6 text-center text-[13px] text-[var(--color-suave)]">
            {q ? `Nada com “${busca}”.` : "Nenhuma conversa neste filtro agora."}
          </p>
        )}
      </div>
    </aside>
  );
}

function Chip({ ativo, onClick, rotulo, n }: { ativo: boolean; onClick: () => void; rotulo: string; n: number }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={ativo}
      className={
        "flex h-[30px] items-center gap-1.5 rounded-full px-3 text-[13px] font-semibold transition-colors " +
        (ativo
          ? "bg-[var(--color-realce)] text-[var(--color-marca-forte)]"
          : "bg-[var(--color-painel)] text-[var(--color-suave)] hover:text-[var(--color-tinta)]")
      }
    >
      {rotulo}
      <span className="num text-[11.5px] opacity-75">{n}</span>
    </button>
  );
}

function Grupo({ titulo, cor, itens }: { titulo: string; cor: string; itens: ItemConversa[] }) {
  const pathname = useRouterState({ select: (r) => r.location.pathname });
  const { data: eu } = useSuspenseQuery(euQuery());
  const naoLida = useNaoLida(eu.clinica.id);
  return (
    <section>
      <h2 className={"rotulo px-2.5 pt-3.5 pb-1.5 " + cor}>
        {titulo} · {itens.length}
      </h2>
      <ul>
        {itens.map((i) => {
          const selecionado = pathname === `/conversas/${i.telefone}`;
          return <Item key={i.telefone} i={i} selecionado={selecionado} nova={!selecionado && naoLida(i.telefone, i.ultimaDoClienteISO)} />;
        })}
      </ul>
    </section>
  );
}

/**
 * Uma conversa na lista. `nova` = o paciente escreveu depois da última vez que
 * alguém a abriu neste navegador: nome em negrito, hora verde e o ponto à
 * direita, como num app de mensagem.
 */
function Item({ i, selecionado, nova }: { i: ItemConversa; selecionado: boolean; nova: boolean }) {
  const nome = comoChamar(i.nome, i.telefone);
  const escrevendo = useEscrevendo(i.telefone) && !i.atendimentoHumano;
  return (
    <li>
      <Link
        to="/conversas/$telefone"
        params={{ telefone: i.telefone }}
        aria-current={selecionado ? "page" : undefined}
        className={
          "flex w-full items-start gap-3 rounded-xl p-2.5 text-left transition-colors " +
          (selecionado ? "bg-[var(--color-realce)]" : "hover:bg-[var(--color-painel)]")
        }
      >
        <Avatar iniciais={i.iniciais} telefone={i.telefone} />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex items-baseline justify-between gap-2">
            <span className={"truncate text-[15px] text-[var(--color-tinta)] " + (nova ? "font-bold" : "font-semibold")}>{nome}</span>
            <time className={"shrink-0 text-xs " + (nova ? "font-bold text-[var(--color-marca-forte)]" : "text-[var(--color-fraco)]")}>{i.quando}</time>
          </div>
          <div className="flex items-center gap-1.5 text-[var(--color-suave)]">
            {!i.janelaAberta && (
              <span title="Janela de 24h fechada" className="shrink-0 text-[var(--color-fraco)]">
                <Icone nome="cadeado" className="h-3.5 w-3.5" />
                <span className="sr-only">Janela de 24h fechada.</span>
              </span>
            )}
            {escrevendo ? (
              <span className="truncate text-[13.5px] text-[var(--color-marca-forte)] italic">Assistente escrevendo…</span>
            ) : (
              <span className={"truncate text-[13.5px] " + (nova ? "font-semibold text-[var(--color-tinta)]" : "")}>{i.preview}</span>
            )}
            {nova && (
              <span className="ml-auto h-2.5 w-2.5 shrink-0 rounded-full bg-[var(--color-marca)]">
                <span className="sr-only">Mensagem nova</span>
              </span>
            )}
          </div>
          {i.pendencia && (
            <p title={i.pendencia.motivo} className="mt-0.5 flex items-center gap-1.5 text-[12.5px] font-semibold text-[var(--color-alerta)]">
              <Icone nome="equipe" className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{i.pendencia.motivo.split("\n").at(-1)}</span>
            </p>
          )}
          {i.etiquetas.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {i.etiquetas.map((e) => (
                <span
                  key={e}
                  className="rounded-md bg-[var(--color-painel)] px-2 py-px text-xs font-medium text-[var(--color-suave)] shadow-[inset_0_0_0_1px_var(--color-borda)]"
                >
                  {e}
                </span>
              ))}
            </div>
          )}
        </div>
      </Link>
    </li>
  );
}
